import { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { flowsApi } from '../api/flows';
import { CopyButton } from '../components/CopyButton';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { PayloadViewer } from '../components/PayloadViewer';
import { PollingIndicator } from '../components/PollingIndicator';
import { RelatedLinks } from '../components/RelatedLinks';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { useAsyncData } from '../hooks/useAsyncData';
import { documentsByCorrelationLink, exchangesByCorrelationLink } from '../lib/crossLinks';
import { MARITIME_DOCUMENT_TYPE_OPTIONS, type FlowExecutionRecord } from '../types/api';

const REPLAYABLE_STATUSES = new Set(['FAILED', 'DEAD_LETTERED']);

const emptyExecutionFilters = {
  partner: '',
  flow: '',
  messageType: '',
  status: '',
  from: '',
  to: '',
  correlationId: '',
};

const PAGE_SIZE = 50;
const EXECUTIONS_POLL_INTERVAL_MS = 20_000;

// Pre-applies filters carried in the incoming URL (e.g. a cross-link from a document,
// exchange, or AS2 message detail page — see src/lib/crossLinks.ts), read once on first
// render, mirroring the pattern already used by the AS2 message list pages.
function initialFiltersFromSearch(search: string): typeof emptyExecutionFilters {
  const params = new URLSearchParams(search);
  return Object.fromEntries(Object.keys(emptyExecutionFilters).map((key) => [key, params.get(key) ?? ''])) as typeof emptyExecutionFilters;
}

export function ExecutionsConsolePage() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(() => initialFiltersFromSearch(search));
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() => ['messageType', 'from', 'to', 'correlationId'].some((key) => new URLSearchParams(search).has(key)));
  const [page, setPage] = useState(() => Math.max(1, Number.parseInt(new URLSearchParams(search).get('page') ?? '1', 10) || 1));
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const [replayReason, setReplayReason] = useState('');
  const [pollingPaused, setPollingPaused] = useState(false);
  const [replayFeedback, setReplayFeedback] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(search);
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value); else params.delete(key);
    });
    if (page > 1) params.set('page', String(page)); else params.delete('page');
    const nextSearch = params.toString();
    if (nextSearch !== new URLSearchParams(search).toString()) navigate({ search: `?${nextSearch}` }, { replace: true });
  }, [filters, navigate, page, search]);

  function applyFilters(next: typeof emptyExecutionFilters) {
    setFilters(next);
    setPage(1);
  }

  const { data, isLoading, error, refresh } = useAsyncData(
    () => flowsApi.executions({
      page,
      pageSize: PAGE_SIZE,
      status: filters.status,
      partner: filters.partner,
      flow: filters.flow,
      correlationId: filters.correlationId,
      messageType: filters.messageType,
      from: filters.from,
      to: filters.to,
    }),
    [page, filters.status, filters.partner, filters.flow, filters.correlationId, filters.messageType, filters.from, filters.to],
    {
      debounceMs: 250,
      pollIntervalMs: pollingPaused ? undefined : EXECUTIONS_POLL_INTERVAL_MS,
      isPollingSuspended: Boolean(confirm),
    },
  );

  const executions = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function buildReplayConfirmState(execution: FlowExecutionRecord, reason: string): ConfirmDialogState {
    return {
      title: 'Replay flow execution',
      message: `This resubmits execution ${execution.id} from its original payload (mode: REPLAY_ORIGINAL). Use after correcting the condition that caused it to fail.`,
      confirmLabel: 'Replay execution',
      risk: 'high',
      detailLabel: 'Reason',
      detailValue: reason,
      detailPlaceholder: 'Why is this execution being replayed?',
      detailRequired: true,
      onDetailChange: (value) => {
        setReplayReason(value);
        setConfirm(buildReplayConfirmState(execution, value));
      },
      onConfirm: async () => {
        const result = await flowsApi.replay(execution.id, reason.trim());
        setReplayFeedback(result.duplicate ? 'A replay was already requested for this execution.' : `Replay ${formatRuntimeKey(result.replay.id)} was requested.`);
        await refresh();
      },
    };
  }

  function requestReplay(execution: FlowExecutionRecord) {
    setReplayReason('');
    setConfirm(buildReplayConfirmState(execution, ''));
  }

  return (
    <div className="page-stack">
      <section className="hero">
        <div>
          <span>EXECUTIONS</span>
          <h1>Execution Monitoring</h1>
          <p>Track flow name, partner, message type, status, correlation, duration, retry count, target, and error reason.</p>
        </div>
        <div className="panel-actions">
          <PollingIndicator paused={pollingPaused} onToggle={() => setPollingPaused((paused) => !paused)} />
          <button type="button" onClick={() => void refresh()}>Refresh</button>
        </div>
      </section>
      <section className="panel" aria-busy={isLoading}>
        <div className="panel-header executions-panel-header">
          <div><h2>Recent executions</h2><p>Open an execution to inspect its input and result before taking action.</p></div>
          {data ? <span className="result-count" aria-live="polite">{total} execution{total === 1 ? '' : 's'}</span> : null}
        </div>
        <div className="toolbar toolbar-grid executions-filter-grid">
          <label className="filter-field">
            <span>Status</span>
            <select value={filters.status} onChange={(event) => applyFilters({ ...filters, status: event.target.value })}>
              <option value="">All statuses</option>
              {['RECEIVED', 'IN_PROGRESS', 'SUCCEEDED', 'FAILED', 'DEAD_LETTERED'].map((status) => <option key={status}>{status}</option>)}
            </select>
          </label>
          <label className="filter-field">
            <span>Partner</span>
            <input placeholder="Partner code or ID" value={filters.partner} onChange={(event) => applyFilters({ ...filters, partner: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Flow</span>
            <input placeholder="Flow name or ID" value={filters.flow} onChange={(event) => applyFilters({ ...filters, flow: event.target.value })} />
          </label>
          {showAdvancedFilters ? <>
          <label className="filter-field">
            <span>Message type</span>
            <select value={filters.messageType} onChange={(event) => applyFilters({ ...filters, messageType: event.target.value })}>
              <option value="">All message types</option>
              {MARITIME_DOCUMENT_TYPE_OPTIONS.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </label>
          <label className="filter-field">
            <span>From</span>
            <input type="date" value={filters.from} onChange={(event) => applyFilters({ ...filters, from: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>To</span>
            <input type="date" value={filters.to} onChange={(event) => applyFilters({ ...filters, to: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Correlation ID</span>
            <input value={filters.correlationId} onChange={(event) => applyFilters({ ...filters, correlationId: event.target.value })} />
          </label>
          </> : null}
          <button type="button" className="ghost-button" aria-expanded={showAdvancedFilters} onClick={() => setShowAdvancedFilters((visible) => !visible)}>
            {showAdvancedFilters ? 'Fewer filters' : `More filters${advancedFilterCount(filters) ? ` (${advancedFilterCount(filters)})` : ''}`}
          </button>
          <button type="button" className="ghost-button" disabled={!Object.values(filters).some(Boolean)} onClick={() => { setPage(1); setFilters(emptyExecutionFilters); }}>Reset filters</button>
        </div>
        <p className="results-status" aria-live="polite">{isLoading ? 'Updating results…' : data ? `Showing ${executions.length} of ${total} executions.` : ''}</p>
        {replayFeedback ? <p className="operation-feedback" role="status">{replayFeedback}</p> : null}
        {isLoading ? <LoadingState label="Loading executions..." /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && !error && executions.length === 0 ? <EmptyState label="No executions matched the current filters." /> : null}
        {executions.length ? <ExecutionTable executions={executions} onReplay={requestReplay} search={search} /> : null}
        {total > PAGE_SIZE ? (
          <div className="pagination-bar">
            <button type="button" className="ghost-button" disabled={page <= 1 || isLoading} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {page} of {totalPages} &mdash; {total} total</span>
            <button type="button" className="ghost-button" disabled={page >= totalPages || isLoading} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        ) : null}
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function ExecutionTable({ executions, onReplay, search }: { executions: FlowExecutionRecord[]; onReplay: (execution: FlowExecutionRecord) => void; search: string }) {
  const [expandedExecutionId, setExpandedExecutionId] = useState<string | null>(null);

  return (
    <div className="table-scroll executions-table-scroll">
    <table className="data-table executions-table">
      <thead><tr><th>Flow</th><th>Partner</th><th>Message</th><th>Status</th><th>Received</th><th>Correlation</th><th>Runtime</th><th>Outcome</th><th>Actions</th></tr></thead>
      <tbody>{executions.map((execution) => {
        const expanded = expandedExecutionId === execution.id;
        return (
          <Fragment key={execution.id}>
            <tr className={expanded ? 'execution-row-expanded' : ''}>
              <td data-label="Flow"><strong>{humanizeRuntimeId(execution.flowId)}</strong><small className="execution-secondary" title={execution.flowId}>{formatRuntimeKey(execution.flowId)}</small></td>
              <td data-label="Partner">{execution.partnerId ? humanizeRuntimeId(execution.partnerId) : 'Not assigned'}</td>
              <td data-label="Message">{readMessageType(execution)}</td>
              <td data-label="Status"><StatusBadge value={execution.status} /></td>
              <td data-label="Received" title={formatRelativeTime(execution.createdAt)}>{new Date(execution.createdAt).toLocaleString('en-GB')}</td>
              <td data-label="Correlation" title={execution.correlationId ?? execution.id} className="inline-copy">
                <span className="mono">{formatRuntimeKey(execution.correlationId ?? execution.id)}</span>
                <CopyButton value={execution.correlationId ?? execution.id} label="Copy correlation ID" />
                {execution.correlationId ? (
                  <RelatedLinks links={[
                    documentsByCorrelationLink(execution.correlationId, search),
                    exchangesByCorrelationLink(execution.correlationId, search),
                  ]}
                  />
                ) : null}
              </td>
              <td data-label="Runtime"><span>{formatDuration(execution)}</span><small className="execution-secondary">{execution.retryHistory.length} {execution.retryHistory.length === 1 ? 'retry' : 'retries'}</small></td>
              <td data-label="Outcome" className={execution.error ? 'execution-error' : ''}>{execution.error ?? 'No error reported'}</td>
              <td data-label="Actions" className="actions-cell execution-actions">
                <button
                  type="button"
                  className="ghost-button"
                  aria-expanded={expanded}
                  onClick={() => setExpandedExecutionId(expanded ? null : execution.id)}
                >
                  {expanded ? 'Close details' : 'View details'}
                </button>
                {REPLAYABLE_STATUSES.has(execution.status) ? (
                  <button type="button" className="danger-button" onClick={() => onReplay(execution)}>
                    Replay
                  </button>
                ) : null}
              </td>
            </tr>
            {expanded ? (
              <tr className="execution-detail-row">
                <td colSpan={9} data-label="Execution details">
                  <div className="execution-detail-meta">
                    <span><strong>Execution ID</strong> <span className="mono">{execution.id}</span></span>
                    <span><strong>Target</strong> {execution.connectorInstanceId ?? execution.connectorVersion ?? 'Not recorded'}</span>
                  </div>
                  <div className="page-grid two-columns">
                    <div className="panel">
                      <h2>Payload received</h2>
                      <PayloadViewer value={execution.payloadSnapshot} filename={`${execution.id}.payload.json`} />
                    </div>
                    <div className="panel">
                      <h2>Payload after flow</h2>
                      <PayloadViewer value={execution.resultSnapshot} filename={`${execution.id}.result.json`} />
                    </div>
                  </div>
                </td>
              </tr>
            ) : null}
          </Fragment>
        );
      })}</tbody>
    </table>
    </div>
  );
}

function advancedFilterCount(filters: typeof emptyExecutionFilters): number {
  return [filters.messageType, filters.from, filters.to, filters.correlationId].filter(Boolean).length;
}

function humanizeRuntimeId(value: string): string {
  return value.replace(/^flow_/, '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatRelativeTime(value: string): string {
  const elapsed = new Date(value).getTime() - Date.now();
  const absolute = Math.abs(elapsed);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [['day', 86_400_000], ['hour', 3_600_000], ['minute', 60_000]];
  const [unit, divisor] = units.find(([, size]) => absolute >= size) ?? ['minute', 60_000];
  if (absolute < 60_000) return 'Just now';
  return new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' }).format(Math.round(elapsed / divisor), unit);
}

function formatDuration(execution: FlowExecutionRecord): string {
  if (!execution.completedAt) return 'In progress';
  const milliseconds = Math.max(0, new Date(execution.completedAt).getTime() - new Date(execution.createdAt).getTime());
  if (milliseconds < 1_000) return `${milliseconds} ms`;
  if (milliseconds < 60_000) return `${(milliseconds / 1_000).toFixed(milliseconds < 10_000 ? 1 : 0)} s`;
  return `${Math.floor(milliseconds / 60_000)}m ${Math.round((milliseconds % 60_000) / 1_000)}s`;
}

function readMessageType(execution: FlowExecutionRecord): string {
  const payloadType = execution.payloadSnapshot['messageType'];
  return typeof payloadType === 'string' ? payloadType : 'n/a';
}

function formatRuntimeKey(value: string): string {
  const [eventType, ...rest] = value.split('-');
  const suffix = rest[rest.length - 1];
  if (suffix && /^\d+$/.test(suffix)) {
    return `${formatLabel(eventType)} - ${suffix}`;
  }

  const normalized = value.replace(/^(flowexec|flow|doc|exg)_/, '$1 ');
  return normalized.length > 26 ? `${normalized.slice(0, 26)}...` : normalized;
}

function formatLabel(value: string): string {
  return value.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
}
